import { Controller } from '@hotwired/stimulus'
import { datadogRum } from '@datadog/browser-rum'

declare const Stripe: any

const STRIPE_UNAVAILABLE_WARNING_TITLE = 'Stripe.js unavailable on checkout'
const STRIPE_UNAVAILABLE_MESSAGE_FALLBACK = 'Payment form could not load. Disable content blockers and reload this page.'

type RollbarBrowser = {
  warning?: (message: string, extra?: Record<string, unknown>) => void
}

declare global {
  interface Window {
    Rollbar?: RollbarBrowser
    __stripeCheckoutUnavailableReported?: boolean
  }
}

type StripeChangeEvent = {
  complete?: boolean
  empty?: boolean
  brand?: string
  error?: { code?: string, message?: string }
}

export type CardState = {
  complete: boolean
  empty: boolean
  changed: boolean
  errorCode: string | null
}

export const INCOMPLETE_CARD_MESSAGE = 'Please complete your credit card details.'
export const REENTER_CARD_MESSAGE = [
  'If the card fields look filled in, your browser or a password manager may have autofilled them',
  'in a way Stripe cannot read: clear them and type the details manually.'
].join(' ')

const INITIAL_STATE: CardState = {
  complete: false, empty: true, changed: false, errorCode: null
}

export default class extends Controller {
  static targets = [
    'cardElement',
    'cardErrors',
    'stripeUnavailableNotice'
  ]

  static values = {
    stripePublishableKey: String,
    mode: String
  }

  declare cardElementTarget: HTMLDivElement
  declare cardErrorsTarget: HTMLDivElement
  declare hasCardErrorsTarget: boolean
  declare stripeUnavailableNoticeTarget: HTMLElement
  declare hasStripeUnavailableNoticeTarget: boolean
  declare stripePublishableKeyValue: string
  declare modeValue: string

  private stripe: any
  private cardElement: any
  private elements: any
  private cardState: CardState = { ...INITIAL_STATE }
  private stripeUnavailable = false

  connect() {
    console.log('[CardComponent] Controller connected', { mode: this.modeValue })
    this.initializeCardElement()
  }

  disconnect() {
    this.destroyCardElement()
  }

  getCardState(): CardState {
    return { ...this.cardState }
  }

  isComplete(): boolean {
    return this.cardState.complete
  }

  private initializeCardElement() {
    if (!this.stripePublishableKeyValue) {
      console.warn('[CardComponent] Stripe publishable key not available')
      return
    }

    if (typeof Stripe === 'undefined') {
      this.handleStripeUnavailable()
      return
    }

    if (!this.stripe) {
      this.stripe = Stripe(this.stripePublishableKeyValue)
    }

    this.cardState = { ...INITIAL_STATE }
    this.elements = this.stripe.elements()
    this.cardElement = this.elements.create('card', { hidePostalCode: true })
    this.cardElement.on('change', this.handleChange)
    this.cardElement.mount(this.cardElementTarget)
  }

  private destroyCardElement() {
    if (!this.cardElement) return

    this.cardElement.off('change', this.handleChange)
    this.cardElement.destroy()
    this.cardElement = null
    this.cardState = { ...INITIAL_STATE }
  }

  private handleChange = (event: StripeChangeEvent): void => {
    this.cardState = {
      complete: Boolean(event.complete),
      empty: Boolean(event.empty),
      changed: true,
      errorCode: event.error?.code || null
    }

    if (event.error?.message) {
      this.showError(event.error.message)
    } else {
      this.clearError()
    }

    this.element.dispatchEvent(new CustomEvent('card-changed', {
      detail: this.getCardState(),
      bubbles: true
    }))
  }

  handleNextAction(clientSecret: string): Promise<any> {
    if (this.stripeUnavailable || !this.stripe) {
      return Promise.reject(new Error(this.revealStripeUnavailableNotice()))
    }
    return this.stripe.handleNextAction({ clientSecret })
  }

  createToken(billingDetails?: any): Promise<any> {
    if (this.stripeUnavailable || !this.stripe || !this.cardElement) {
      const message = this.stripeUnavailable ? this.revealStripeUnavailableNotice() : 'Stripe not initialized'
      console.warn('[CardComponent] Stripe not initialized')
      return Promise.reject(message)
    }

    const tokenData: any = {}
    if (billingDetails) {
      tokenData.name = billingDetails.name
      tokenData.address_line1 = billingDetails.address?.line1 || ''
      tokenData.address_line2 = billingDetails.address?.line2 || ''
      tokenData.address_city = billingDetails.address?.city || ''
      tokenData.address_state = billingDetails.address?.state || ''
      tokenData.address_zip = billingDetails.address?.postal_code || ''
      tokenData.address_country = billingDetails.address?.country || ''
      console.log('[CardComponent] Creating token with billing details:', tokenData)
    } else {
      console.log('[CardComponent] Creating token without billing details')
    }

    const stateAtSubmit = this.getCardState()

    return this.stripe.createToken(this.cardElement, tokenData).then((result: any) => {
      if (result.error) {
        this.showError(this.messageFor(result.error, stateAtSubmit))
        return { error: result.error }
      }

      console.log('[CardComponent] Token created successfully:', result.token)
      this.clearError()
      return { token: result.token }
    })
  }

  private messageFor(error: StripeChangeEvent['error'] & { type?: string }, state: CardState): string {
    const message = error.message || INCOMPLETE_CARD_MESSAGE
    if (error.type !== 'validation_error') return message

    this.reportValidationError(error.code, state)
    // An untouched, empty form is a plain "fill it in"; anything else may be unreadable autofill.
    const untouched = !state.changed || state.empty
    return untouched ? message : `${message} ${REENTER_CARD_MESSAGE}`
  }

  private reportValidationError(code: string | undefined, state: CardState): void {
    const detail = { code: code || 'unknown', ...state, mode: this.modeValue }
    if (state.complete) {
      console.warn('[CardComponent] Stripe validation disagreed with tracked card state', detail)
    }
    try {
      datadogRum.addAction('stripe_card_validation_error', detail)
    } catch {
      // RUM is optional; never let telemetry break checkout.
    }
  }

  showError(message: string): void {
    if (this.hasCardErrorsTarget) {
      this.cardErrorsTarget.textContent = message
    }
  }

  clearError(): void {
    if (this.hasCardErrorsTarget) {
      this.cardErrorsTarget.textContent = ''
    }
  }

  private handleStripeUnavailable(): void {
    this.stripeUnavailable = true
    this.clearError()
    this.revealStripeUnavailableNotice()
    this.disableSubmitButtons()
    this.reportStripeUnavailable()
  }

  private revealStripeUnavailableNotice(): string {
    const message = this.hasStripeUnavailableNoticeTarget
      ? (this.stripeUnavailableNoticeTarget.textContent?.trim() || STRIPE_UNAVAILABLE_MESSAGE_FALLBACK)
      : STRIPE_UNAVAILABLE_MESSAGE_FALLBACK

    if (this.hasStripeUnavailableNoticeTarget) {
      this.stripeUnavailableNoticeTarget.classList.remove('hide')
    }

    return message
  }

  private disableSubmitButtons(): void {
    this.element.closest('form')?.querySelectorAll<HTMLInputElement | HTMLButtonElement>('[type="submit"]').forEach((submitButton) => {
      submitButton.disabled = true
    })
  }

  private reportStripeUnavailable(): void {
    if (window.__stripeCheckoutUnavailableReported) return

    window.__stripeCheckoutUnavailableReported = true
    const warning = window.Rollbar?.warning
    if (!warning) return

    warning(STRIPE_UNAVAILABLE_WARNING_TITLE, {
      component: 'card',
      mode: this.modeValue,
      path: window.location.pathname
    })
  }
}
